import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityCard } from "./recovery";

interface Activity {
  id: string;
  title: string;
  description: string;
  stripeColor?: string;
}

interface ActivitiesPopupProps {
  weekNumber: number;
  activities: Activity[];
  activitiesCompleted: Record<string, boolean>;
  onToggle: (activityId: string) => void;
  onClose: () => void;
}

const ActivitiesPopup: React.FC<ActivitiesPopupProps> = ({
  weekNumber,
  activities,
  activitiesCompleted,
  onToggle,
  onClose,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  const completedCount = activities.filter((a) => activitiesCompleted[a.id]).length;
  const total = activities.length;

  // Track screen size
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Animate in on mount
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    requestAnimationFrame(() => {
      sheet.style.transform = isDesktop ? "scale(1)" : "translateY(0)";
      sheet.style.opacity = "1";
    });
  }, [isDesktop]);

  // Close with animation
  const handleClose = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return onClose();
    sheet.style.transform = isDesktop ? "scale(0.97)" : "translateY(100%)";
    sheet.style.opacity = "0";
    setTimeout(onClose, 280);
  }, [onClose, isDesktop]);

  // Close on overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) handleClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        background: "rgba(12, 38, 36, 0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isDesktop ? "center" : "flex-end",
        justifyContent: isDesktop ? "center" : "stretch",
      }}
    >
      <div
        ref={sheetRef}
        style={{
          width: isDesktop ? "calc(100% - 48px)" : "100%",
          maxWidth: isDesktop ? 860 : "100%",
          height: isDesktop ? "calc(100vh - 48px)" : "auto",
          maxHeight: isDesktop ? "calc(100vh - 48px)" : "85vh",
          background: "#f7f9f5",
          borderRadius: isDesktop ? 24 : "28px 28px 0 0",
          display: "flex",
          flexDirection: "column",
          transform: isDesktop ? "scale(0.97)" : "translateY(100%)",
          opacity: 0,
          transition: "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease",
          overflow: "hidden",
        }}
      >
        {!isDesktop && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, paddingBottom: 4 }}>
            <div style={{ width: 36, height: 4, borderRadius: 9999, background: "#d1d9d8" }} />
          </div>
        )}

        <div style={{ padding: isDesktop ? "24px 32px 20px" : "12px 24px 16px", borderBottom: "1px solid #e8eeec" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "#1e5e5b", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "DM Sans, sans-serif", marginBottom: 2 }}>
                Week {weekNumber}
              </p>
              <h2 style={{ margin: 0, fontSize: isDesktop ? 24 : 20, fontWeight: 700, color: "#0c2624", fontFamily: "DM Sans, sans-serif" }}>
                Weekly Activities
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                border: "none",
                background: "#e8eeec",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e5e5b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 999, background: "#dce8e6", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, #1e5e5b, #2e7d5b)",
                  width: total > 0 ? `${(completedCount / total) * 100}%` : "0%",
                  transition: "width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
              />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e5e5b", fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap" }}>
              {completedCount}/{total}
            </span>
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: isDesktop ? "24px 32px 40px" : "16px 24px 32px" }}>
          {activities.length === 0 ? (
            <p style={{ textAlign: "center", color: "#94a3b8", fontFamily: "DM Sans, sans-serif", marginTop: 32 }}>
              No activities for this week yet.
            </p>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr",
              gap: isDesktop ? 20 : 16,
            }}>
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  title={activity.title}
                  description={activity.description}
                  // FIX: Added nullish coalescing to provide a string fallback
                  stripeColor={activity.stripeColor ?? ""}
                  isCompleted={activitiesCompleted[activity.id] ?? false}
                  onToggle={() => onToggle(activity.id)}
                  isReadOnly={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivitiesPopup;